import React, { useEffect, useState, useCallback } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import AvatarWithLabel from "views_shared/app/javascript/components/AvatarWithLabel";
import { normalizeProfileType, getProfileTypeBadge } from "views_shared/app/javascript/utils/profileTypeHelpers";
import ProfileTypeConversionModal from "./ProfileTypeConversionModal";
import MediaProfileConfigModal from "../shared/MediaProfileConfigModal";

// Steel blue "garish" color used for admin styling throughout the app
const COLOR_ADMIN = "#4682B4";

function effectiveActionLabel(configData) {
  if (!configData) return "No override";
  if (configData.id)
    return configData.auto_action === "defer" ? "defer to outlet" : configData.auto_action.replace("_", " ");
  // Synthesized from user status (no explicit config record)
  if (configData.effective_profile_action === "manual_review") return "manual review (default)";
  if (configData.effective_profile_action === "ghost") return "ghost (disabled)";
  return configData.effective_profile_action?.replace("_", " ") || "No override";
}

function LoadingState() {
  return (
    <div className="admin-user-header mb-5 p-4 bg-white border rounded shadow-sm">
      <div className="d-flex align-items-center">
        <div className="rounded-circle bg-secondary me-4" style={{ width: "100px", height: "100px" }} />
        <div>
          <div className="placeholder-glow">
            <span className="placeholder col-6" style={{ width: "200px", height: "32px" }} />
          </div>
          <div className="placeholder-glow mt-2">
            <span className="placeholder col-4" style={{ width: "150px" }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="alert alert-danger mb-4" role="alert">
      <strong>Error loading user header:</strong> {message}
    </div>
  );
}

/**
 * AdminUserHeader - Shared header component for admin user pages
 *
 * Displays user avatar, name, type badges, and quick action buttons.
 * Used on both Overview and Notifications tabs.
 */
function AdminUserHeader({ userSlug, userId }) {
  const [userProfile, setUserProfile] = useState(null);
  const [userData, setUserData] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);

  const fetchConfigData = useCallback(async () => {
    const baseUrl = globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;
    const headers = new Headers(authenticatedHeadersHash());
    const res = await fetch(`${baseUrl}/rest/v1/admin_user_profiles?slug=eq.${userSlug}`, { headers });
    if (res.ok) {
      const data = await res.json();
      if (data.length > 0) {
        const row = data[0];
        if (row.config_id != null) {
          setConfigData({
            id: row.config_id,
            auto_action: row.auto_action,
            effective_profile_action: row.effective_profile_action,
            vetted: row.vetted,
          });
        } else if (row.effective_profile_action != null) {
          setConfigData({
            id: null,
            auto_action: null,
            effective_profile_action: row.effective_profile_action,
            vetted: row.vetted,
          });
        } else {
          setConfigData(null);
        }
      } else {
        setConfigData(null);
      }
    }
  }, [userSlug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const baseUrl = globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;
        const headers = new Headers(authenticatedHeadersHash());

        const [profileRes, userRes, adminProfileRes] = await Promise.all([
          fetch(`${baseUrl}/rest/v1/user_profiles?slug=eq.${userSlug}`, { headers }),
          fetch(`${baseUrl}/rest/v1/users?id=eq.${userId}`, { headers }),
          fetch(`${baseUrl}/rest/v1/admin_user_profiles?slug=eq.${userSlug}`, { headers }),
        ]);

        if (!profileRes.ok || !userRes.ok) {
          throw new Error("Failed to fetch user data");
        }

        const [profiles, users, adminProfiles] = await Promise.all([
          profileRes.json(),
          userRes.json(),
          adminProfileRes.ok ? adminProfileRes.json() : [],
        ]);

        if (profiles.length === 0) {
          throw new Error("User profile not found");
        }

        setUserProfile(profiles[0]);
        setUserData(users[0] || null);

        if (adminProfiles.length > 0) {
          const row = adminProfiles[0];
          if (row.config_id != null) {
            setConfigData({
              id: row.config_id,
              auto_action: row.auto_action,
              effective_profile_action: row.effective_profile_action,
              vetted: row.vetted,
            });
          } else if (row.effective_profile_action != null) {
            setConfigData({
              id: null,
              auto_action: null,
              effective_profile_action: row.effective_profile_action,
              vetted: row.vetted,
            });
          }
        }

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [userSlug, userId]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  const avatarProfileType = normalizeProfileType(userProfile.type_of_profile);
  const profileTypeBadge = getProfileTypeBadge(avatarProfileType);

  // Admin levels based on permissions enum:
  // 0 = no_permissions (regular user), 1 = admin, 99 = superadmin
  const permissions = userData?.permissions ?? 0;
  const isSuperadmin = permissions === 99;
  const isAdmin = permissions >= 1;

  // Determine which badge to show
  let userBadge;
  if (isSuperadmin) {
    userBadge = (
      <span className="badge fs-5" style={{ backgroundColor: COLOR_ADMIN }}>
        <i className="fa fa-star me-1" />
        Superadmin
      </span>
    );
  } else if (isAdmin) {
    userBadge = (
      <span className="badge fs-5" style={{ backgroundColor: COLOR_ADMIN }}>
        <i className="fa fa-shield me-1" />
        Admin
      </span>
    );
  } else {
    userBadge = (
      <span
        className="badge fs-5"
        style={{ backgroundColor: profileTypeBadge.backgroundColor, color: profileTypeBadge.textColor }}
      >
        {profileTypeBadge.label}
      </span>
    );
  }

  return (
    <div className="admin-user-header mb-5 p-4 bg-white border rounded shadow-sm">
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <div className="me-4">
            <AvatarWithLabel
              profileType={avatarProfileType}
              src={userProfile.profile_image_url}
              alt={userProfile.name}
              size={100}
            />
          </div>
          <div>
            <div className="d-flex align-items-center mb-2">
              <h1 className="display-6 mb-0 me-3">{userProfile.name}</h1>
              {userBadge}
            </div>
            {userProfile.team_name && (
              <div className="mb-2">
                <span className="badge bg-secondary fs-6">Team: {userProfile.team_name}</span>
              </div>
            )}
            <div className="text-muted">
              <i className="fa fa-envelope me-2" />
              {userData?.email}
            </div>
            {userProfile.headline && (
              <div className="text-muted mt-1">
                <small>{userProfile.headline}</small>
              </div>
            )}
            {!isAdmin && userProfile.type_of_profile === "type_media" && (
              <div className="text-muted mt-1 d-flex align-items-center gap-2">
                <small>
                  <i className="fa fa-cog me-1" />
                  Auto Action: <strong>{effectiveActionLabel(configData)}</strong>
                </small>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm py-0 px-1"
                  style={{ fontSize: "0.75rem" }}
                  onClick={() => setShowConfigModal(true)}
                >
                  Configure
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-start">
          {!isAdmin && (
            <a href={`/admin/users/${userSlug}/impersonate`} className="btn btn-primary btn-sm">
              <i className="fa fa-user-secret me-1" />
              Impersonate
            </a>
          )}
          <a
            href={`/profiles/${userSlug}`}
            className="btn btn-outline-primary btn-sm"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fa fa-eye me-1" />
            View Profile
          </a>
          {userData?.migrate_id && (
            <a
              href={`${globalThis.Qwoted.V1_URL}/users/${userData.migrate_id}`}
              className="btn btn-outline-secondary btn-sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa fa-external-link me-1" />
              V1
            </a>
          )}
          {userData?.migrate_id && globalThis.Qwoted.INTERCOM_APP_ID && (
            <a
              href={`https://app.intercom.io/apps/${globalThis.Qwoted.INTERCOM_APP_ID}/users/show?user_id=${userData.migrate_id}`}
              className="btn btn-outline-secondary btn-sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa fa-comments me-1" />
              Intercom
            </a>
          )}
          {userData?.hubspot_id && globalThis.Qwoted.HUBSPOT_HUB_ID && (
            <a
              href={`https://app.hubspot.com/contacts/${globalThis.Qwoted.HUBSPOT_HUB_ID}/record/0-1/${userData.hubspot_id}`}
              className="btn btn-outline-secondary btn-sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa fa-address-book me-1" />
              HubSpot
            </a>
          )}
          {!isAdmin && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => setShowConversionModal(true)}
            >
              <i className="fa fa-exchange me-1" />
              Switch Profile Type
            </button>
          )}
        </div>
      </div>

      {!isAdmin && (
        <ProfileTypeConversionModal
          show={showConversionModal}
          onHide={() => setShowConversionModal(false)}
          userId={userId}
          currentProfileType={userProfile.type_of_profile}
        />
      )}

      {showConfigModal && (
        <MediaProfileConfigModal
          profileId={userProfile.profile_id}
          profileName={userProfile.name}
          profileType="Id::Profile::MediaProfile"
          currentConfig={configData}
          onSave={() => {
            setShowConfigModal(false);
            fetchConfigData();
          }}
          onClose={() => setShowConfigModal(false)}
        />
      )}
    </div>
  );
}

export default AdminUserHeader;
